import { useContext, useMemo, useState } from 'react'
import { mdiFileOutline, mdiPencilOutline, mdiTrashCanOutline } from '@mdi/js'
import { FormattedMessage, useIntl } from 'react-intl'
import { DateTime } from 'luxon'

import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import TextWithShowMore from 'components/TextWithShowMore'
import type { JobNoteType } from 'features/jobs/types'
import JobEditNote from '../../JobEditNote'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { JobPageContext } from 'features/jobs/JobPageContext'
import { jobInfoNotesSelector } from 'features/jobs/selectors'
import { getAuthorizedUser } from 'app/services/LocalStorage'
import { DATE_FORMAT_FOR_NOTES } from 'features/jobs/components/JobInfo/consts'
import { fetchJobInfoThunk, updateJobThunk } from 'features/jobs/slice'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { commonMessages } from 'app/i18n/messages'
import { RegularModerate } from 'components/Typography'
import { jobInfoNotesMessages } from 'features/jobs/components/JobInfo/JobInfoNotes/messages'
import { SpinnerCentered } from 'components/Loader'
import { JobNoteBlock } from '../../styles'
import {
  JobNoteBody,
  JobNoteButton,
  JobNoteGreyIcon,
  JobNoteHeader,
  JobNoteHeaderLeftBlock,
  JobNoteHeaderRightBlock,
  JobNoteHeaderRightBlockDelimiter,
  JobNoteHeaderRightBlockEmail,
  JobNoteIcon,
  JobNoteModalBlock,
  JobNoteModalSection,
} from './styles'

interface JobNoteProps {
  jobNote: JobNoteType
}

function JobNote({ jobNote }: JobNoteProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { jobId } = useContext(JobPageContext)
  const initialNotes = useAppSelector(jobInfoNotesSelector)
  const noteDate = useMemo(
    () =>
      DateTime.fromFormat(
        jobNote.editDate || jobNote.creationDate,
        DATE_FORMAT_FOR_NOTES,
        { zone: 'utc' }
      ).toMillis(),
    [jobNote.editDate, jobNote.creationDate]
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)

  const handleEditNote = async (updatedText: string) => {
    const storedUser = getAuthorizedUser()
    if (!updatedText || updatedText.length === 0 || !storedUser?.email) {
      return
    }
    try {
      setIsLoading(true)
      const date = DateTime.utc().toFormat(DATE_FORMAT_FOR_NOTES).toString()
      const updatedNote: JobNoteType = {
        authorEmail: jobNote.authorEmail,
        editorEmail: storedUser.email,
        creationDate: jobNote.creationDate,
        editDate: date,
        text: updatedText,
      }

      const newNotes = initialNotes.map((oldNote) =>
        oldNote.text === jobNote.text &&
        oldNote.creationDate === jobNote.creationDate
          ? updatedNote
          : oldNote
      )

      await dispatch(
        updateJobThunk({
          domainId: jobId,
          payload: { notes: newNotes },
        })
      )
      await dispatch(fetchJobInfoThunk(jobId))
      setIsLoading(false)
      setEditMode(false)
    } catch (err) {
      setIsLoading(false)
      setIsError(true)
      console.log('Error updating job note', err)
    }
  }

  const handleDeleteJobNote = async () => {
    try {
      setIsLoading(true)
      const newNotes = initialNotes.filter(
        (oldNote) =>
          oldNote.text !== jobNote.text &&
          oldNote.creationDate !== jobNote.creationDate
      )
      await dispatch(
        updateJobThunk({
          domainId: jobId,
          payload: { notes: newNotes },
        })
      )
      await dispatch(fetchJobInfoThunk(jobId))
      setIsLoading(false)
      setEditMode(false)
      setIsOpenDeleteModal(false)
    } catch (err) {
      setIsLoading(false)
      setIsError(true)
      console.log('Error deleting job note', err)
    }
  }

  return (
    <>
      <JobNoteBlock>
        {editMode ? (
          <JobEditNote
            text={jobNote.text}
            isError={isError}
            isLoading={isLoading}
            handleClose={() => setEditMode(false)}
            handleSubmit={handleEditNote}
          />
        ) : (
          <>
            <JobNoteHeader>
              <JobNoteHeaderLeftBlock>
                <JobNoteGreyIcon
                  path={mdiFileOutline}
                  rotate={90}
                  vertical={true}
                />
                {noteDate && <IntlFormattedDate date={noteDate} withTime />}
                <JobNoteHeaderRightBlockDelimiter>
                  /
                </JobNoteHeaderRightBlockDelimiter>
                <JobNoteHeaderRightBlockEmail>
                  {jobNote.authorEmail}
                </JobNoteHeaderRightBlockEmail>
              </JobNoteHeaderLeftBlock>

              <JobNoteHeaderRightBlock>
                <JobNoteButton onClick={() => setEditMode(true)}>
                  <JobNoteIcon path={mdiPencilOutline} />
                </JobNoteButton>
                <JobNoteButton onClick={() => setIsOpenDeleteModal(true)}>
                  <JobNoteIcon path={mdiTrashCanOutline} />
                </JobNoteButton>
              </JobNoteHeaderRightBlock>
            </JobNoteHeader>

            <JobNoteBody>
              <TextWithShowMore text={jobNote.text} />
            </JobNoteBody>
          </>
        )}
      </JobNoteBlock>

      {isOpenDeleteModal && (
        <ConfirmModal
          isOpen
          onDiscard={() => setIsOpenDeleteModal(false)}
          onConfirm={handleDeleteJobNote}
          title={formatMessage(jobInfoNotesMessages.deleteModalTitle)}
          discardBtnTitle={formatMessage(commonMessages.cancel)}
          confirmBtnTitle={formatMessage(commonMessages.delete)}
          variant="errorFilled"
        >
          <JobNoteModalSection>
            <RegularModerate>
              {formatMessage(jobInfoNotesMessages.deleteModalMessage)}
            </RegularModerate>
            {isError && (
              <JobNoteModalBlock>
                <RegularModerate>
                  <FormattedMessage {...commonMessages.commonError} />
                </RegularModerate>
              </JobNoteModalBlock>
            )}
            {isLoading && (
              <JobNoteModalBlock>
                <SpinnerCentered size="24" />
              </JobNoteModalBlock>
            )}
          </JobNoteModalSection>
        </ConfirmModal>
      )}
    </>
  )
}

export default JobNote
